import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Check, Lock, Minus } from "lucide-react";
import { Checkbox, Input, SegmentedControl, Switch, cn, type SegmentedOption } from "@nexus/ui";
import { accessLevel } from "@/features/identity-hub/lib/permission-access";
import {
  areaAccessCounts,
  permissionEditorSections,
  type PermissionEditorSection,
} from "@/features/identity-hub/lib/permission-nav-groups";
import {
  FEATURE_AREAS,
  featureAreaKeys,
  filterFeatureAreasByKeys,
  type ActionGroup,
  type FeatureArea,
  type PermDef,
} from "@/features/identity-hub/lib/permission-catalog";
import {
  effectiveLockedCatalogKeys,
  isPlatformOnlyCatalogKey,
} from "@/features/identity-hub/hooks/usePermissionSelection";

export interface PermissionAccessCardsProps {
  selected: Set<string>;
  onTogglePerm: (key: string) => void;
  onToggleGroup: (keys: string[]) => void;
  onRemoveKeys: (keys: string[]) => void;
  /** Search driven by the caller. When omitted, the editor renders its own search field. */
  filter?: string;
  showSelectedOnly: boolean;
  readOnly?: boolean;
  /** Catalog keys the viewer may grant; `null` or omitted shows the whole catalog. */
  grantableKeys?: ReadonlySet<string> | null;
  /** Catalog keys already on the role that the viewer cannot revoke. Shown on, with Off disabled. */
  lockedKeys?: ReadonlySet<string>;
  /** Overrides the default sizing, e.g. to fill a fixed-height dialog. */
  className?: string;
}

type AccessChoice = "off" | "on" | "custom";

const AREA_LABELS_BY_KEY: ReadonlyMap<string, string[]> = (() => {
  const labels = new Map<string, string[]>();
  for (const area of FEATURE_AREAS) {
    for (const key of new Set(featureAreaKeys(area))) {
      labels.set(key, [...(labels.get(key) ?? []), area.label]);
    }
  }
  return labels;
})();

interface AccessActions {
  setArea: (area: FeatureArea, choice: AccessChoice) => void;
  setGroup: (area: FeatureArea, group: ActionGroup, choice: AccessChoice) => void;
  setPage: (area: FeatureArea, on: boolean) => void;
  setPerm: (area: FeatureArea, perm: PermDef, on: boolean, group?: ActionGroup) => void;
}

interface AccessView {
  t: TFunction;
  readOnly: boolean;
  isGranted: (key: string) => boolean;
  isLocked: (key: string) => boolean;
  choiceOf: (customId: string, keys: string[]) => AccessChoice;
  /** Custom detail stays visible while editing. Always open in read-only so granted permissions stay listed. */
  isOpen: (customId: string, keys: string[]) => boolean;
  lockedReason: (keys: string[]) => string | undefined;
}

interface SearchHit {
  area: FeatureArea;
  groupLabel: string;
  perm: PermDef;
}

/**
 * Prefers 560px and shrinks with the dialog's flex-column body, so on short viewports the rail
 * and pane scroll side by side instead of the dialog body scrolling around them.
 */
const EDITOR_SIZE_CLASS = "min-h-0 flex-[1_1_560px]";
const MIN_KEYS_FOR_CUSTOM = 2;

export function PermissionAccessFooter({
  selected,
  grantableKeys,
  lockedKeys,
}: {
  selected: Set<string>;
  grantableKeys?: ReadonlySet<string> | null;
  lockedKeys?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const counts = areaAccessCounts(
    unionSelected(selected, lockedKeys),
    editorCatalog(grantableKeys, lockedKeys),
  );
  return (
    <span className="text-xs text-muted-foreground">
      {t(
        "iam.permissionEditor.footerAccessSummary",
        "Areas: {{on}} on · {{custom}} custom · {{off}} off",
        { on: counts.full, custom: counts.partial, off: counts.none },
      )}
    </span>
  );
}

export function PermissionAccessCards({
  selected,
  onTogglePerm,
  onToggleGroup,
  onRemoveKeys,
  filter,
  showSelectedOnly,
  readOnly = false,
  grantableKeys,
  lockedKeys,
  className,
}: PermissionAccessCardsProps) {
  const { t } = useTranslation();
  const [ownQuery, setOwnQuery] = useState("");
  const [activeAreaId, setActiveAreaId] = useState<string | null>(null);
  const [customIds, setCustomIds] = useState<ReadonlySet<string>>(() => new Set());
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => new Set());

  const catalog = useMemo(() => editorCatalog(grantableKeys, lockedKeys), [grantableKeys, lockedKeys]);
  const displaySelected = useMemo(() => unionSelected(selected, lockedKeys), [selected, lockedKeys]);
  const effectiveLockedKeys = useMemo(
    () => effectiveLockedCatalogKeys(lockedKeys, displaySelected),
    [lockedKeys, displaySelected],
  );
  const sections = useMemo(
    () => visibleSections(permissionEditorSections(catalog), displaySelected, showSelectedOnly),
    [catalog, displaySelected, showSelectedOnly],
  );

  const areas = sections.flatMap((section) => section.areas);
  const activeArea =
    areas.find((area) => area.id === activeAreaId) ??
    areas.find((area) => accessLevel(displaySelected, featureAreaKeys(area)) !== "none") ??
    areas[0];
  const query = (filter ?? ownQuery).trim().toLowerCase();

  const setCustom = (id: string, isCustom: boolean) => {
    setCustomIds((prev) => withMembership(prev, id, isCustom));
    if (!isCustom) setOpenIds((prev) => withMembership(prev, id, false));
  };

  /** After an edit inside a Custom block, let the label follow the real state but keep the block open. */
  const keepOpen = (id: string) => {
    setCustomIds((prev) => withMembership(prev, id, false));
    setOpenIds((prev) => withMembership(prev, id, true));
  };

  const grant = (keys: string[]) => {
    const missing = keys.filter((key) => !displaySelected.has(key));
    if (readOnly || missing.length === 0) return;
    if (missing.length === 1) onTogglePerm(missing[0]!);
    else onToggleGroup(missing);
  };

  const revoke = (keys: string[]) => {
    const granted = keys.filter((key) => selected.has(key) && !effectiveLockedKeys.has(key));
    if (readOnly || granted.length === 0) return;
    onRemoveKeys(granted);
  };

  const applyChoice = (customId: string, keys: string[], choice: AccessChoice) => {
    setCustom(customId, choice === "custom");
    if (choice === "on") grant(keys);
    if (choice === "off") revoke(keys);
  };

  const actions: AccessActions = {
    setArea: (area, choice) => applyChoice(areaCustomId(area), featureAreaKeys(area), choice),
    setGroup: (area, group, choice) => {
      const keys = group.perms.map((perm) => perm.key);
      keepOpen(areaCustomId(area));
      applyChoice(groupCustomId(area, group), choice === "on" ? withPageKey(area, keys) : keys, choice);
    },
    setPage: (area, on) => {
      if (!area.pagePerm) return;
      keepOpen(areaCustomId(area));
      if (on) {
        grant([area.pagePerm.key]);
        return;
      }
      for (const group of area.actionGroups ?? []) setCustom(groupCustomId(area, group), false);
      revoke([area.pagePerm.key, ...areaActionKeys(area)]);
    },
    setPerm: (area, perm, on, group) => {
      keepOpen(areaCustomId(area));
      if (group) keepOpen(groupCustomId(area, group));
      if (on) grant(withPageKey(area, [perm.key]));
      else revoke([perm.key]);
    },
  };

  const view: AccessView = {
    t,
    readOnly,
    isGranted: (key) => displaySelected.has(key),
    isLocked: (key) => effectiveLockedKeys.has(key),
    choiceOf: (customId, keys) => {
      const level = accessLevel(displaySelected, keys);
      if (level === "partial" || customIds.has(customId)) return "custom";
      return level === "full" ? "on" : "off";
    },
    isOpen: (customId, keys) =>
      readOnly || openIds.has(customId) || view.choiceOf(customId, keys) === "custom",
    lockedReason: (keys) => lockedReason(t, keys.filter((key) => effectiveLockedKeys.has(key)), lockedKeys),
  };

  const handleSelectArea = (areaId: string) => {
    setActiveAreaId(areaId);
    setOwnQuery("");
  };

  return (
    <div className={cn("flex flex-col gap-3", EDITOR_SIZE_CLASS, className)}>
      {filter === undefined && (
        <Input
          type="search"
          iconStart="search"
          value={ownQuery}
          onChange={(event) => setOwnQuery(event.target.value)}
          placeholder={t("iam.permissionEditor.filterPlaceholder", "Search permissions…")}
          aria-label={t("iam.permissionEditor.searchLabel", "Search permissions")}
          className="h-8 text-xs"
        />
      )}
      <div className="flex min-h-16 flex-1 flex-col overflow-hidden rounded-lg border border-border sm:flex-row">
        <AreaRail
          sections={sections}
          activeAreaId={activeArea?.id}
          view={view}
          onSelectArea={handleSelectArea}
        />
        <div className="min-w-0 flex-1 overflow-y-auto overscroll-contain">
          {query ? (
            <SearchResults
              hits={searchHits(areas, query, displaySelected, showSelectedOnly, t)}
              query={query}
              view={view}
              actions={actions}
            />
          ) : activeArea ? (
            <AreaPane
              area={activeArea}
              view={view}
              actions={actions}
              showSelectedOnly={showSelectedOnly}
              displaySelected={displaySelected}
            />
          ) : (
            <p className="px-6 py-10 text-center text-xs text-muted-foreground">
              {t("iam.permissionEditor.nothingSelected", "No permissions selected yet.")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function AreaRail({
  sections,
  activeAreaId,
  view,
  onSelectArea,
}: {
  sections: PermissionEditorSection[];
  activeAreaId: string | undefined;
  view: AccessView;
  onSelectArea: (areaId: string) => void;
}) {
  const { t } = view;
  return (
    <nav
      aria-label={t("iam.permissionEditor.areasLabel", "Permission areas")}
      className="flex max-h-44 w-full shrink-0 flex-col overflow-y-auto overscroll-contain border-b border-border bg-muted py-2 sm:max-h-none sm:w-64 sm:border-b-0 sm:border-r"
    >
      {sections.map((section) => (
        <div key={section.id} className="flex flex-col">
          <span className="px-4 pb-1 pt-2.5 text-[11px] font-medium uppercase tracking-label text-muted-foreground">
            {t(section.labelKey, section.label)}
          </span>
          {section.areas.length === 0 ? (
            <span className="px-4 py-1.5 text-xs text-muted-foreground">
              {t("iam.permissionEditor.sectionEmpty", "Nothing to grant here yet")}
            </span>
          ) : (
            section.areas.map((area) => {
              const isActive = area.id === activeAreaId;
              return (
                <button
                  key={area.id}
                  type="button"
                  onClick={() => onSelectArea(area.id)}
                  aria-current={isActive ? "true" : undefined}
                  className={cn(
                    "flex min-h-11 items-center gap-2.5 border-l-2 px-3.5 py-1.5 text-left outline-none transition-colors",
                    "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40",
                    isActive
                      ? "border-primary bg-background font-medium text-primary"
                      : "border-transparent text-foreground hover:bg-background/60",
                  )}
                >
                  <span className="min-w-0 flex-1 break-words text-[13px] leading-snug">{area.label}</span>
                  <ChoiceStatus choice={view.choiceOf(areaCustomId(area), featureAreaKeys(area))} t={t} />
                </button>
              );
            })
          )}
        </div>
      ))}
    </nav>
  );
}

function AreaPane({
  area,
  view,
  actions,
  showSelectedOnly,
  displaySelected,
}: {
  area: FeatureArea;
  view: AccessView;
  actions: AccessActions;
  showSelectedOnly: boolean;
  displaySelected: ReadonlySet<string>;
}) {
  const { t, readOnly } = view;
  const areaKeys = featureAreaKeys(area);
  const choice = view.choiceOf(areaCustomId(area), areaKeys);
  const groups = (area.actionGroups ?? []).flatMap((group) => {
    const perms = visiblePerms(group.perms, showSelectedOnly, displaySelected);
    if (perms.length === 0) return [];
    return [{ ...group, perms }];
  });
  const pagePerm =
    area.pagePerm && visiblePerm(area.pagePerm, showSelectedOnly, displaySelected) ? area.pagePerm : undefined;

  return (
    <section aria-label={area.label} className="flex flex-col">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-background px-5 py-4">
        <div className="flex min-w-0 flex-1 flex-col">
          <h3 className="text-[15px] font-semibold text-foreground">{area.label}</h3>
          <p className="text-xs text-muted-foreground">
            {t("iam.permissionEditor.areaQuestion", "What can this role do in {{area}}?", { area: area.label })}
          </p>
        </div>
        <AccessControl
          label={t("iam.permissionEditor.areaAccessLabel", "{{name}} access", { name: area.label })}
          choice={choice}
          keys={areaKeys}
          view={view}
          onChange={(next) => actions.setArea(area, next)}
        />
      </div>

      {view.isOpen(areaCustomId(area), areaKeys) ? (
        <>
          {pagePerm && <PageAccessRow area={area} pagePerm={pagePerm} view={view} actions={actions} />}
          {groups.map((group) => (
            <GroupBlock key={group.id} area={area} group={group} view={view} actions={actions} />
          ))}
        </>
      ) : (
        <div className="flex flex-col gap-2 px-5 py-4">
          <p className="text-xs text-muted-foreground">
            {choice === "on"
              ? t("iam.permissionEditor.areaAllOn", "Everything in {{area}} is on.", { area: area.label })
              : t("iam.permissionEditor.areaAllOff", "Nothing in {{area}} is on.", { area: area.label })}
            {!readOnly && groups.length > 0 && (
              <> {t("iam.permissionEditor.chooseCustomHint", "Choose Custom to pick groups.")}</>
            )}
          </p>
          {groups.length > 0 && (
            <ul className="flex flex-col text-xs text-muted-foreground">
              {groups.map((group) => (
                <li key={group.id} className="flex justify-between border-b border-border-subtle py-1.5 last:border-b-0">
                  <span className="text-foreground">{group.label}</span>
                  <span className="tabular-nums">
                    {t("iam.permissionEditor.actionCount", { count: group.perms.length, defaultValue: "{{count}} actions" })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function PageAccessRow({
  area,
  pagePerm,
  view,
  actions,
}: {
  area: FeatureArea;
  pagePerm: PermDef;
  view: AccessView;
  actions: AccessActions;
}) {
  const { t, readOnly } = view;
  const isOn = view.isGranted(pagePerm.key);
  const reason = view.isLocked(pagePerm.key) ? view.lockedReason([pagePerm.key]) : undefined;
  const title = t("iam.permissionEditor.pageAccessTitle", "Open the {{area}} page", { area: area.label });
  const switchId = useId();
  const copy = (
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="text-[13px] font-medium text-foreground">{title}</span>
      <span className="text-xs text-muted-foreground">
        {reason ??
          t(
            "iam.permissionEditor.pageAccessHint",
            "Turns on with any group. Turning it off also turns off every group.",
          )}
      </span>
      <SharedKeyHint permKey={pagePerm.key} areaLabel={area.label} t={t} />
    </div>
  );

  if (readOnly) {
    return (
      <div className="flex min-h-11 items-center gap-3 border-b border-border px-5 py-3">
        {copy}
        <ChoiceStatus choice={isOn ? "on" : "off"} t={t} />
      </div>
    );
  }
  return (
    <label
      htmlFor={switchId}
      className={cn(
        "flex min-h-11 items-center gap-3 border-b border-border px-5 py-3",
        reason ? "cursor-not-allowed" : "cursor-pointer",
      )}
    >
      {copy}
      <Switch
        id={switchId}
        checked={isOn}
        disabled={Boolean(reason)}
        aria-label={title}
        onCheckedChange={(checked) => actions.setPage(area, checked)}
      />
    </label>
  );
}

function GroupBlock({
  area,
  group,
  view,
  actions,
}: {
  area: FeatureArea;
  group: ActionGroup;
  view: AccessView;
  actions: AccessActions;
}) {
  const { t } = view;
  const keys = group.perms.map((perm) => perm.key);
  const choice = view.choiceOf(groupCustomId(area, group), keys);

  return (
    <div className="flex flex-col border-b border-border">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
        <span className="min-w-0 flex-1 text-[13px] font-medium text-foreground">{group.label}</span>
        <AccessControl
          label={t("iam.permissionEditor.groupAccessLabel", "{{name}} access", { name: group.label })}
          choice={choice}
          keys={keys}
          view={view}
          onChange={(next) => actions.setGroup(area, group, next)}
        />
      </div>
      {view.isOpen(groupCustomId(area, group), keys) && (
        <ul className="mx-5 mb-3 flex flex-col rounded-md bg-muted px-3 py-1.5">
          {group.perms.map((perm) => (
            <li key={perm.key}>
              <PermissionRow
                perm={perm}
                areaLabel={area.label}
                view={view}
                onChange={(on) => actions.setPerm(area, perm, on, group)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SearchResults({
  hits,
  query,
  view,
  actions,
}: {
  hits: SearchHit[];
  query: string;
  view: AccessView;
  actions: AccessActions;
}) {
  const { t } = view;
  if (hits.length === 0) {
    return (
      <p className="px-6 py-10 text-center text-xs text-muted-foreground">
        {t("iam.permissionEditor.noSearchResults", "No permissions match “{{query}}”.", { query })}
      </p>
    );
  }
  return (
    <section className="flex flex-col px-5 py-3">
      <p className="pb-2 text-xs text-muted-foreground" aria-live="polite">
        {t("iam.permissionEditor.searchResultCount", { count: hits.length, defaultValue: "{{count}} matching permissions" })}
      </p>
      <ul className="flex flex-col">
        {hits.map(({ area, groupLabel, perm }) => (
          <li key={`${area.id}:${groupLabel}:${perm.key}`} className="border-b border-border-subtle last:border-b-0">
            <PermissionRow
              perm={perm}
              areaLabel={area.label}
              location={t("iam.permissionEditor.searchLocation", "{{area}} › {{group}}", {
                area: area.label,
                group: groupLabel,
              })}
              view={view}
              onChange={(on) =>
                area.pagePerm?.key === perm.key
                  ? actions.setPage(area, on)
                  : actions.setPerm(area, perm, on)
              }
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function PermissionRow({
  perm,
  areaLabel,
  location,
  view,
  onChange,
}: {
  perm: PermDef;
  areaLabel: string;
  location?: string;
  view: AccessView;
  onChange: (on: boolean) => void;
}) {
  const { t, readOnly } = view;
  const isOn = view.isGranted(perm.key);
  const reason = view.isLocked(perm.key) ? view.lockedReason([perm.key]) : undefined;
  const checkboxId = useId();
  const copy = (
    <div className="flex min-w-0 flex-col">
      <span className="flex items-center gap-1 text-[13px] text-foreground">
        {perm.name}
        {isPlatformOnlyCatalogKey(perm.key) && (
          <Lock
            role="img"
            aria-label={t("iam.permissionEditor.platformOnly", "Platform-only")}
            className="size-3 shrink-0 text-muted-foreground"
          />
        )}
      </span>
      <span className="text-xs text-muted-foreground">{reason ?? perm.description}</span>
      <SharedKeyHint permKey={perm.key} areaLabel={areaLabel} t={t} />
      {location && <span className="text-[11px] text-muted-foreground">{location}</span>}
    </div>
  );

  if (readOnly) {
    return (
      <div className="flex min-h-11 items-start gap-2.5 py-2">
        <GrantedIcon isOn={isOn} t={t} />
        {copy}
      </div>
    );
  }
  return (
    <label
      htmlFor={checkboxId}
      className={cn("flex min-h-11 items-start gap-2.5 py-2", reason ? "cursor-not-allowed" : "cursor-pointer")}
    >
      <Checkbox
        id={checkboxId}
        checked={isOn}
        disabled={Boolean(reason)}
        aria-label={
          reason
            ? t("iam.permissionEditor.lockedCannotRemove", "Can't remove {{name}}. {{reason}}", {
                name: perm.name,
                reason,
              })
            : perm.name
        }
        onCheckedChange={(checked) => onChange(checked === true)}
        className="mt-0.5"
      />
      {copy}
    </label>
  );
}

function AccessControl({
  label,
  choice,
  keys,
  view,
  onChange,
}: {
  label: string;
  choice: AccessChoice;
  keys: string[];
  view: AccessView;
  onChange: (choice: AccessChoice) => void;
}) {
  const { t, readOnly } = view;
  if (readOnly) return <ChoiceStatus choice={choice} t={t} />;

  const lockedReason = view.lockedReason(keys);
  const options: SegmentedOption[] = [
    { value: "off", label: choiceLabel(t, "off"), disabled: Boolean(lockedReason), title: lockedReason },
    { value: "on", label: choiceLabel(t, "on") },
  ];
  if (keys.length >= MIN_KEYS_FOR_CUSTOM) options.push({ value: "custom", label: choiceLabel(t, "custom") });

  return (
    <div className="flex shrink-0 flex-col items-end gap-1">
      <SegmentedControl
        value={choice}
        onValueChange={(next) => onChange(toAccessChoice(next))}
        options={options}
        ariaLabel={label}
        tone="subtle"
        accent="primary"
      />
      {lockedReason && <p className="max-w-60 text-right text-[11px] text-muted-foreground">{lockedReason}</p>}
    </div>
  );
}

/**
 * One grant can back several areas (Partner Portal reuses the Pre Flight – Scripts viewer keys),
 * so ticking it here also moves the other area's control. Say so where it happens.
 */
function SharedKeyHint({ permKey, areaLabel, t }: { permKey: string; areaLabel: string; t: TFunction }) {
  const otherAreas = (AREA_LABELS_BY_KEY.get(permKey) ?? []).filter((label) => label !== areaLabel);
  if (otherAreas.length === 0) return null;
  return (
    <span className="text-[11px] text-muted-foreground">
      {t("iam.permissionEditor.sharedWith", "Also in {{areas}}. Changing it here changes it there too.", {
        areas: otherAreas.join(", "),
      })}
    </span>
  );
}

function ChoiceStatus({ choice, t }: { choice: AccessChoice; t: TFunction }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-md px-2 py-0.5 text-[11px]",
        choice === "on" && "bg-primary-subtle font-medium text-primary-subtle-foreground",
        choice === "custom" && "bg-warning-subtle font-medium text-warning-subtle-foreground",
        choice === "off" && "text-muted-foreground",
      )}
    >
      {choiceLabel(t, choice)}
    </span>
  );
}

function GrantedIcon({ isOn, t }: { isOn: boolean; t: TFunction }) {
  const label = isOn
    ? t("iam.permissionEditor.statusGranted", "Granted")
    : t("iam.permissionEditor.statusNotGranted", "Not granted");
  const Glyph = isOn ? Check : Minus;
  return (
    <span role="img" aria-label={label} className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
      <Glyph className={cn("size-3.5", isOn ? "text-primary" : "text-muted-foreground")} />
    </span>
  );
}

function choiceLabel(t: TFunction, choice: AccessChoice): string {
  if (choice === "on") return t("iam.permissionEditor.accessOn", "On");
  if (choice === "custom") return t("iam.permissionEditor.accessCustom", "Custom");
  return t("iam.permissionEditor.accessOff", "Off");
}

function toAccessChoice(value: string): AccessChoice {
  return value === "on" || value === "custom" ? value : "off";
}

function lockedReason(
  t: TFunction,
  locked: string[],
  originalLockedKeys: ReadonlySet<string> | undefined,
): string | undefined {
  if (locked.length === 0) return undefined;
  const original = locked.filter((key) => originalLockedKeys?.has(key));
  if (original.some(isPlatformOnlyCatalogKey)) {
    return t(
      "iam.permissionEditor.platformOnlyLocked",
      "Platform-only action. You can't remove this. Create a new role without it, or ask a platform admin.",
    );
  }
  if (original.length > 0) {
    return t(
      "iam.permissionEditor.ungrantableLocked",
      "You can't remove this action because you don't have it yourself. Create a new role without it, or ask a platform admin.",
    );
  }
  return t(
    "iam.permissionEditor.requiredByLockedAction",
    "This page stays on because this role still has actions you can't remove.",
  );
}

function searchHits(
  areas: FeatureArea[],
  query: string,
  selected: ReadonlySet<string>,
  showSelectedOnly: boolean,
  t: TFunction,
): SearchHit[] {
  const pageLabel = t("iam.permissionEditor.pageAccess", "Page access");
  const candidates = areas.flatMap((area) => [
    ...(area.pagePerm ? [{ area, groupLabel: pageLabel, perm: area.pagePerm }] : []),
    ...(area.actionGroups ?? []).flatMap((group) =>
      group.perms.map((perm) => ({ area, groupLabel: group.label, perm })),
    ),
  ]);
  return candidates.filter(({ area, groupLabel, perm }) => {
    if (showSelectedOnly && !selected.has(perm.key)) return false;
    return `${perm.name} ${perm.description} ${area.label} ${groupLabel}`.toLowerCase().includes(query);
  });
}

function visibleSections(
  sections: PermissionEditorSection[],
  selected: ReadonlySet<string>,
  showSelectedOnly: boolean,
): PermissionEditorSection[] {
  if (!showSelectedOnly) return sections;
  return sections.map((section) => ({
    ...section,
    areas: section.areas.filter((area) => accessLevel(selected, featureAreaKeys(area)) !== "none"),
  }));
}

function visiblePerm(
  perm: PermDef,
  showSelectedOnly: boolean,
  selected: ReadonlySet<string>,
): boolean {
  return !showSelectedOnly || selected.has(perm.key);
}

function visiblePerms(
  perms: PermDef[],
  showSelectedOnly: boolean,
  selected: ReadonlySet<string>,
): PermDef[] {
  if (!showSelectedOnly) return perms;
  return perms.filter((perm) => selected.has(perm.key));
}

function withMembership(set: ReadonlySet<string>, id: string, isMember: boolean): ReadonlySet<string> {
  if (set.has(id) === isMember) return set;
  const next = new Set(set);
  if (isMember) next.add(id);
  else next.delete(id);
  return next;
}

function areaCustomId(area: FeatureArea): string {
  return `area:${area.id}`;
}

function groupCustomId(area: FeatureArea, group: ActionGroup): string {
  return `group:${area.id}:${group.id}`;
}

function areaActionKeys(area: FeatureArea): string[] {
  return (area.actionGroups ?? []).flatMap((group) => group.perms.map((perm) => perm.key));
}

function withPageKey(area: FeatureArea, keys: string[]): string[] {
  return area.pagePerm ? [area.pagePerm.key, ...keys] : keys;
}

function editorCatalog(
  grantableKeys: ReadonlySet<string> | null | undefined,
  lockedKeys?: ReadonlySet<string>,
): FeatureArea[] {
  if (!grantableKeys) return FEATURE_AREAS;
  if (!lockedKeys || lockedKeys.size === 0) return filterFeatureAreasByKeys(FEATURE_AREAS, grantableKeys);
  const keys = new Set(grantableKeys);
  for (const key of lockedKeys) keys.add(key);
  return filterFeatureAreasByKeys(FEATURE_AREAS, keys);
}

function unionSelected(selected: Set<string>, lockedKeys: ReadonlySet<string> | undefined): Set<string> {
  if (!lockedKeys || lockedKeys.size === 0) return selected;
  const next = new Set(selected);
  for (const key of lockedKeys) next.add(key);
  return next;
}
